// src/stores/employeeSalaryStore.js
import { defineStore } from "pinia";
import employeeSalaryService from "@/services/hrm/employee-salary.service";
import payrollService from "@/services/payroll.service";
import reportsService from "@/services/reports.service";
import { createScopedCache } from "@/stores/_cache";
import { toast } from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

function errMsg(err) {
  return err?.response?.data?.message || err?.message || "Unexpected error";
}

function splitCacheParams(params = {}) {
  const { force = false, ...requestParams } = params || {};
  return { force, requestParams };
}

const payrollRunCache = createScopedCache("employee-salary:payroll-run");
const payslipReportCache = createScopedCache("employee-salary:payslip-report");
const p9ListCache = createScopedCache("employee-salary:p9-list");
const bankTransferReportCache = createScopedCache(
  "employee-salary:bank-transfer-report",
);
const musterRollReportCache = createScopedCache(
  "employee-salary:muster-roll-report",
);
const statutoryReportCache = createScopedCache(
  "employee-salary:statutory-report",
);
const p10ReportCache = createScopedCache("employee-salary:p10-report");
const payrollComparisonCache = createScopedCache(
  "employee-salary:payroll-comparison-report",
);
const payrollControlTotalsCache = createScopedCache(
  "employee-salary:payroll-control-totals",
);
const loansSavingsReportCache = createScopedCache(
  "employee-salary:loans-savings-report",
);
const myPayslipsCache = createScopedCache("employee-salary:my-payslips");
const myP9YearsCache = createScopedCache("employee-salary:my-p9-years");
const memberSavingsReportCache = createScopedCache(
  "employee-salary:member-savings-report",
);
const loanDisbursementsReportCache = createScopedCache(
  "employee-salary:loan-disbursements-report",
);
const loanRepaymentsReportCache = createScopedCache(
  "employee-salary:loan-repayments-report",
);
const monthlyCollectionsReportCache = createScopedCache(
  "employee-salary:monthly-collections-report",
);
const interestEarnedReportCache = createScopedCache(
  "employee-salary:interest-earned-report",
);

const PAYROLL_RUN_TTL = 60 * 1000;
const REPORT_TTL = 90 * 1000;
const SHORT_REPORT_TTL = 60 * 1000;

export const useEmployeeSalaryStore = defineStore("employeeSalary", {
  state: () => ({
    salaries: [],
    employees: [],
    currentSalary: null,
    loading: false,
    error: null,
    payrollResults: [],
    payrollRun: null,

    // ⬇️ Payslip report state
    payslipSummary: null, // { total_gross, total_deductions, total_net, total_allowances, currency, from, to }
    payslipSeries: [], // [{period,label,gross,deductions,net,allowances}, ...]
    payslipRows: [], // table rows (current page)
    payslipTotal: 0,
    payslipPageSize: 25,
    payslipCurrent: 1,
    payslipLoading: false,

    // P9 report state
    p9Rows: [],
    p9Year: new Date().getFullYear(),
    p9Total: 0,
    p9PageSize: 25,
    p9Current: 1,
    p9Loading: false,

    // Bank transfer report state
    bankTransferSummary: null,
    bankTransferTemplates: [],
    bankTransferRows: [],   
    bankTransferLoading: false,

    // Statutory deduction report state
    statutorySummary: null,
    statutoryRows: [],
    statutoryLoading: false,

    // P10 report state
    p10Summary: null,
    p10Rows: [],
    p10Loading: false,

    // Muster roll report state
    musterSummary: null,
    musterRows: [],
    musterTotal: 0,
    musterPageSize: 25,
    musterCurrent: 1,
    musterLoading: false,

    // Payroll comparison report state
    payrollComparisonSummary: null,
    payrollComparisonRows: [],
    payrollComparisonLoading: false,

    // Control total report state
    controlSummary: null,
    controlStatutory: [],
    controlOther: [],
    controlPaymentModes: [],
    controlLoading: false,

    // Loans & Savings report state
    loansSavingsSummary: null,
    loansSavingsRows: [],
    loansSavingsTotal: 0,
    loansSavingsPageSize: 25,
    loansSavingsCurrent: 1,
    loansSavingsLoading: false,

    //mirroring loans & savings above //commenting out for now

    // loansSavingsRows: [],
    // loansSavingsSummary: null,
    // loansSavingsLoading: false,
    // loansSavingsTotal: 0,
    // loansSavingsPageSize: 25,
    // loansSavingsCurrent: 1,
    // loansSavingsLoading: false,

    //mirroring loans & savings for employee view
    myLoansSavingsRows: [],
    myLoansSavingsSummary: null,
    myLoansSavingsLoading: false,
    myLoansSavingsTotal: 0,
    myLoansSavingsPageSize: 25,
    myLoansSavingsCurrent: 1,

    // ⬇️ My Payslips (employee)
    myPayslipSummary: null,
    myPayslipSeries: [],
    myPayslipRows: [],
    myPayslipTotal: 0,
    myPayslipPageSize: 25,
    myPayslipCurrent: 1,
    myPayslipLoading: false,

    // My p9(Employee)
    myP9Years: [],
    myP9DefaultYear: new Date().getFullYear(),
    myP9Loading: false,
    myP9Downloading: false,

    // =========================
    // NEW REPORTS (Loans/Savings)
    // =========================

    // Member Savings
    memberSavingsSummary: null,
    memberSavingsRows: [],
    memberSavingsTotal: 0,
    memberSavingsPageSize: 25,
    memberSavingsCurrent: 1,
    memberSavingsLoading: false,

    // Loan Disbursements
    loanDisbursementsSummary: null,
    loanDisbursementsRows: [],
    loanDisbursementsTotal: 0,
    loanDisbursementsPageSize: 25,
    loanDisbursementsCurrent: 1,
    loanDisbursementsLoading: false,

    // Loan Repayments
    loanRepaymentsSummary: null,
    loanRepaymentsRows: [],
    loanRepaymentsTotal: 0,
    loanRepaymentsPageSize: 25,
    loanRepaymentsCurrent: 1,
    loanRepaymentsLoading: false,

    // Monthly Collections (no pagination - backend returns rows array)
    monthlyCollectionsSummary: null,
    monthlyCollectionsRows: [],
    monthlyCollectionsLoading: false,

    // Interest Earned (rows array)
    interestEarnedSummary: null,
    interestEarnedRows: [],
    interestEarnedLoading: false,
  }),

  actions: {
    async fetchSalaries(params = {}) {
      if (!(await guardPlanModule(MODULE_KEYS.HRM_PAYROLL))) {
        this.salaries = [];
        return;
      }

      this.loading = true;
      try {
        const { data } = await employeeSalaryService.list(params);
        this.salaries = data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
      } finally {
        this.loading = false;
      }
    },

    async createSalary(payload, companyId = null) {
      this.loading = true;
      try {
        const { data } = await employeeSalaryService.create(payload, companyId);
        this.salaries.unshift(data);
        toast("Salary record added successfully!");
        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
      } finally {
        this.loading = false;
      }
    },

    async fetchSalary(id, params = {}) {
      this.loading = true;
      try {
        const { data } = await employeeSalaryService.show(id, params);
        this.currentSalary = data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async updateSalary(id, payload) {
      this.loading = true;
      try {
        const { data } = await employeeSalaryService.update(id, payload);
        const idx = this.salaries.findIndex((s) => s.id === id);
        if (idx !== -1) this.salaries[idx] = data;
        toast("Salary record updated successfully!");
        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async deleteSalary(id) {
      this.loading = true;
      try {
        await employeeSalaryService.destroy(id);
        this.salaries = this.salaries.filter((s) => s.id !== id);
        toast("Salary record deleted successfully!");
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Run payroll for a period and optional list of employee IDs.
     * Accepts opts: { email_payslips?: boolean, force?: boolean }
     */

    async runPayroll(payloadOrPeriod, ids, opts) {
      if (this.loading) return;
      this.loading = true;
      this.error = null;
      try {
        const payload =
          typeof payloadOrPeriod === "object"
            ? payloadOrPeriod
            : {
                period: payloadOrPeriod,
                frequency: "monthly",
                employee_ids: ids || [],
                ...(opts || {}),
              };
        console.log("[Store] /payroll/run payload:", payload);
        payrollRunCache.invalidate({
          period: payload.period,
          period_code: payload.period_code,
          frequency: payload.frequency,
        });
        const { data } = await payrollService.runPayroll(payload);
        this.payrollRun = data?.run || null;
        this.payrollResults = Array.isArray(data) ? data : data.results || [];
        payrollRunCache.invalidate({
          period: payload.period,
          period_code: payload.period_code || data?.run?.period_code,
          frequency: payload.frequency || data?.run?.frequency,
        });
        const isForce = !!payload.force;
        if (isForce) toast("Payroll generated successfully!");

        return data;
      } catch (err) {
        const status = err?.response?.status;
        this.error = err?.response?.data?.message || err.message;
        if (status === 409) {
          toast(this.error || "Payroll run already in progress", "warning");
        } else {
          toast(this.error || "Failed to generate payroll", "error");
        }
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async approvePayroll(payload) {
      if (this.loading) return;
      this.loading = true;
      this.error = null;
      try {
        payrollRunCache.invalidate({
          period: payload?.period,
          period_code: payload?.period_code,
        });
        const { data } = await payrollService.approvePayroll(payload);
        this.payrollRun = data?.run || null;
        this.payrollResults = data?.results || [];
        payrollRunCache.invalidate({
          period: payload?.period,
          period_code: payload?.period_code || data?.run?.period_code,
        });
        toast(data?.message || "Payroll approved successfully!");
        return data;
      } catch (err) {
        this.error = err?.response?.data?.message || err.message;
        toast(this.error || "Failed to approve payroll", "error");
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async fetchPayrollRun(params, options = {}) {
      if (this.loading && options.force !== true) return;
      this.loading = true;
      this.error = null;
      try {
        const requestPayload =
          typeof params === "object" && params !== null
            ? params
            : { period: params };
        const { force, requestParams } = splitCacheParams({
          ...requestPayload,
          force: options.force === true,
        });
        const data = await payrollRunCache.get(requestParams, {
          ttlMs: PAYROLL_RUN_TTL,
          force,
          loader: async () => {
            const response = await payrollService.fetchRun(requestParams);
            return response.data;
          },
        });
        this.payrollRun = data?.run || null;
        this.payrollResults = data?.results || [];
        return data;
      } catch (err) {
        // Avoid showing a previous month's results after a 404 / failed switch.
        this.payrollRun = null;
        this.payrollResults = [];
        this.error = err?.response?.data?.message || err.message;
        toast(this.error || "Failed to load payroll run", "error");
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async fetchLoanSavingsMetaHint(employeeId, period) {
      try {
        const { data } = await employeeSalaryService.getLoanSavingsMetaHint(
          employeeId,
          period,
        );
        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      }
    },

    async fetchPayrollAdjustments(employeeId, period) {
      try {
        const { data } = await employeeSalaryService.getPayrollAdjustments(
          employeeId,
          period,
        );
        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      }
    },

    async savePayrollAdjustments(payload) {
      try {
        const { data } = await employeeSalaryService.savePayrollAdjustments(
          payload,
        );
        toast("Payroll adjustments saved successfully!");
        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      }
    },

    async resendPayslipEmail(payslipId) {
      try {
        await payrollService.resendPayslipEmail(payslipId);
        toast("Payslip email sent.", "success");
      } catch (err) {
        const m = errMsg(err);
        toast(m, "error");
        throw err;
      }
    },

    async fetchPayslipReport(params = {}) {
      // params: { frequency?, from_date: 'YYYY-MM-DD', to_date: 'YYYY-MM-DD', page, per_page }
      if (this.payslipLoading) return;
      this.payslipLoading = true;
      this.error = null;
      try {
        const { force, requestParams } = splitCacheParams(params);
        const data = await payslipReportCache.get(requestParams, {
          ttlMs: REPORT_TTL,
          force,
          loader: async () => {
            const response = await reportsService.payslips(requestParams);
            return response.data;
          },
        });
        // summary & series
        this.payslipSummary = data?.summary || null;
        this.payslipSeries = Array.isArray(data?.series) ? data.series : [];

        // table pagination (Laravel paginator)
        const rows = data?.rows || {};
        this.payslipRows = rows.data || [];
        this.payslipTotal = rows.total || 0;
        this.payslipPageSize = rows.per_page || requestParams.per_page || 25;
        this.payslipCurrent = rows.current_page || requestParams.page || 1;

        console.log("Payslip report data:", data);

        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      } finally {
        this.payslipLoading = false;
      }
    },

    async exportPayslipReportCSV(params = {}) {
      try {
        const res = await reportsService.exportPayslipsCSV(params);
        const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const from = params.from_date || "start";
        const to = params.to_date || "end";
        a.download = `payslip-report-${from}_to_${to}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },
    async exportPayslipReportPDF(params = {}) {
      try {
        const res = await reportsService.exportPayslipsPDF(params);
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const from = params.from_date || "start";
        const to = params.to_date || "end";
        a.download = `payslip-report-${from}_to_${to}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },

    async fetchP9List(params = {}) {
      if (this.p9Loading) return;
      this.p9Loading = true;
      try {
        const { force, requestParams } = splitCacheParams(params);
        const data = await p9ListCache.get(requestParams, {
          ttlMs: REPORT_TTL,
          force,
          loader: async () => {
            const response = await reportsService.p9List(requestParams);
            return response.data;
          },
        });
        const rows = data.rows || {};
        this.p9Rows = rows.data || [];
        this.p9Total = rows.total || 0;
        this.p9PageSize = rows.per_page || requestParams.per_page || 25;
        this.p9Current = rows.current_page || requestParams.page || 1;
        this.p9Year = data.year;
        return data;
      } finally {
        this.p9Loading = false;
      }
    },

    async downloadP9(employeeId, year) {
      const res = await reportsService.p9Download(employeeId, year);
      const url = URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `P9_${employeeId}_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    async emailP9(employeeId, year) {
      await reportsService.p9Email(employeeId, year);
    },
    async bulkEmailP9({ year, employeeIds = null } = {}) {
      const payload = { year };
      if (Array.isArray(employeeIds) && employeeIds.length) {
        payload.employee_ids = employeeIds;
      }
      const { data } = await reportsService.p9BulkEmail(payload);
      return data;
    },
    async exportP9Zip(year) {
      const res = await reportsService.p9ExportZip(year);
      const url = URL.createObjectURL(
        new Blob([res.data], { type: "application/zip" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `P9_${year}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },

    async exportP9Csv(year) {
      const res = await reportsService.p9ExportCsv(year);
      const url = URL.createObjectURL(
        new Blob([res.data], { type: "text/csv" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `P9_${year}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },

    async downloadPayslipReport(format = "csv", params = {}) {
      try {
        const svc =
          format === "pdf"
            ? reportsService.exportPayslipsPDF
            : reportsService.exportPayslipsCSV;

        const res = await svc(params);

        const mime =
          format === "pdf" ? "application/pdf" : "text/csv;charset=utf-8";

        const blob = new Blob([res.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        const from = params.from_date || "start";
        const to = params.to_date || "end";
        const ext = format === "pdf" ? "pdf" : "csv";

        a.href = url;
        a.download = `payslip-report-${from}_to_${to}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },

    async fetchBankTransferReport(params = {}) {
      if (this.bankTransferLoading) return;
      this.bankTransferLoading = true;
      this.error = null;
      try {
        const { force, requestParams } = splitCacheParams(params);
        const data = await bankTransferReportCache.get(requestParams, {
          ttlMs: REPORT_TTL,
          force,
          loader: async () => {
            const response = await reportsService.bankTransfer(requestParams);
            return response.data;
          },
        });
        this.bankTransferSummary = data.summary || null;
        this.bankTransferRows = data.rows || [];
        this.bankTransferTemplates = data.templates || [];
        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      } finally {
        this.bankTransferLoading = false;
      }
    },

    async exportBankTransferReport(format = "csv", params = {}) {
      try {
        const exportParams = { ...params };
        // template_id=0 → force Standard (DoneQuick); omit key → server uses company default template

        let svc = reportsService.exportBankTransferCSV;
        if (format === "pdf") svc = reportsService.exportBankTransferPDF;
        if (format === "xlsx") svc = reportsService.exportBankTransferXlsx;

        const res = await svc(exportParams);
        const mime =
          format === "pdf"
            ? "application/pdf"
            : format === "xlsx"
              ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              : "text/csv;charset=utf-8";

        const blob = new Blob([res.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        const period =
          params.period ||
          [params.frequency || "period", params.period_start, params.period_end]
            .filter(Boolean)
            .join("-");
        const ext = format === "pdf" ? "pdf" : format === "xlsx" ? "xlsx" : "csv";

        a.href = url;
        a.download = `bank-transfer-${period}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },

    async fetchMusterRollReport(params = {}) {
      // params: { period: 'YYYY-MM', page, per_page, department_id?, employee_id? }
      if (this.musterLoading) return;
      this.musterLoading = true;
      this.error = null;

      try {
        const { force, requestParams } = splitCacheParams(params);
        const data = await musterRollReportCache.get(requestParams, {
          ttlMs: REPORT_TTL,
          force,
          loader: async () => {
            const response = await reportsService.musterRoll(requestParams);
            return response.data;
          },
        });

        this.musterSummary = data.summary || null;

        const rows = data.rows || {};
        if (Array.isArray(rows)) {
          // if you ever decide to return plain array instead of paginator
          this.musterRows = rows;
          this.musterTotal = rows.length;
          this.musterPageSize = requestParams.per_page || 25;
          this.musterCurrent = requestParams.page || 1;
        } else {
          this.musterRows = rows.data || [];
          this.musterTotal = rows.total || 0;
          this.musterPageSize = rows.per_page || requestParams.per_page || 25;
          this.musterCurrent = rows.current_page || requestParams.page || 1;
        }

        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      } finally {
        this.musterLoading = false;
      }
    },

    async exportMusterRollReportCSV(params = {}) {
      try {
        const res = await reportsService.exportMusterRollCSV(params);
        const blob = new Blob([res.data], {
          type: "text/csv;charset=utf-8",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        const period =
          params.period ||
          [params.frequency || "period", params.period_start, params.period_end]
            .filter(Boolean)
            .join("-");
        a.href = url;
        a.download = `muster-roll-${period}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },

    async exportMusterRollReportExcel(params = {}) {
      try {
        const res = await reportsService.exportMusterRollExcel(params);
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        const period =
          params.period ||
          [params.frequency || "period", params.period_start, params.period_end]
            .filter(Boolean)
            .join("-");
        a.href = url;
        a.download = `Muster_Roll_${period}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },

    async exportMusterRollReportPDF(params = {}) {
      try {
        const res = await reportsService.exportMusterRollPDF(params);
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        const period =
          params.period ||
          [params.frequency || "period", params.period_start, params.period_end]
            .filter(Boolean)
            .join("-");
        a.href = url;
        a.download = `muster-roll-${period}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },

    async fetchStatutoryReport(params = {}) {
      if (this.statutoryLoading) return;
      this.statutoryLoading = true;
      this.error = null;
      try {
        const { force, requestParams } = splitCacheParams(params);
        const data = await statutoryReportCache.get(requestParams, {
          ttlMs: REPORT_TTL,
          force,
          loader: async () => {
            const response =
              await reportsService.statutoryDeductions(requestParams);
            return response.data;
          },
        });
        this.statutorySummary = data.summary || null;
        this.statutoryRows = data.rows || [];
        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      } finally {
        this.statutoryLoading = false;
      }
    },

    async exportStatutoryReport(format = "csv", params = {}) {
      try {
        const svc =
          format === "pdf"
            ? reportsService.exportStatutoryDeductionsPDF
            : reportsService.exportStatutoryDeductionsCSV;

        const res = await svc(params);
        const mime =
          format === "pdf" ? "application/pdf" : "text/csv;charset=utf-8";

        const blob = new Blob([res.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        const period = params.period || "period";
        const ext = format === "pdf" ? "pdf" : "csv";

        a.href = url;
        a.download = `statutory-deductions-${period}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },

    async fetchP10Report(params = {}) {
      if (this.p10Loading) return;
      this.p10Loading = true;
      this.error = null;
      try {
        const { force, requestParams } = splitCacheParams(params);
        const data = await p10ReportCache.get(requestParams, {
          ttlMs: REPORT_TTL,
          force,
          loader: async () => {
            const response = await reportsService.p10(requestParams);
            return response.data;
          },
        });
        this.p10Summary = data.summary || null;
        this.p10Rows = data.rows || [];
        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      } finally {
        this.p10Loading = false;
      }
    },

    async exportP10Report(format = "csv", params = {}) {
      try {
        const svc =
          format === "pdf"
            ? reportsService.exportP10PDF
            : reportsService.exportP10CSV;

        const res = await svc(params);
        const mime =
          format === "pdf" ? "application/pdf" : "text/csv;charset=utf-8";

        const blob = new Blob([res.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        const period =
          params.period ||
          [params.frequency || "period", params.period_start, params.period_end]
            .filter(Boolean)
            .join("-");
        const ext = format === "pdf" ? "pdf" : "csv";

        a.href = url;
        a.download = `p10-${period}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },

    async fetchPayrollComparison(params = {}) {
      if (this.payrollComparisonLoading) return;
      this.payrollComparisonLoading = true;
      this.error = null;
      try {
        const { force, requestParams } = splitCacheParams(params);
        const data = await payrollComparisonCache.get(requestParams, {
          ttlMs: REPORT_TTL,
          force,
          loader: async () => {
            const response = await reportsService.payrollComparison(requestParams);
            return response.data;
          },
        });
        this.payrollComparisonSummary = data.summary || null;
        this.payrollComparisonRows = data.rows || [];
        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      } finally {
        this.payrollComparisonLoading = false;
      }
    },

    async exportPayrollComparisonReport(format = "csv", params = {}) {
      try {
        const svc =
          format === "pdf"
            ? reportsService.exportPayrollComparisonPDF
            : reportsService.exportPayrollComparisonCSV;

        const res = await svc(params);
        const mime =
          format === "pdf" ? "application/pdf" : "text/csv;charset=utf-8";

        const blob = new Blob([res.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        const prev =
          params.previous_period ||
          params.previous_period_start ||
          "prev";
        const curr =
          params.current_period ||
          params.current_period_start ||
          "curr";
        const ext = format === "pdf" ? "pdf" : "csv";

        a.href = url;
        a.download = `payroll-comparison-${prev}_vs_${curr}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },

    async fetchPayrollControlTotals(params = {}) {
      if (this.controlLoading) return;
      this.controlLoading = true;
      this.error = null;
      try {
        const { force, requestParams } = splitCacheParams(params);
        const data = await payrollControlTotalsCache.get(requestParams, {
          ttlMs: SHORT_REPORT_TTL,
          force,
          loader: async () => {
            const response =
              await reportsService.payrollControlTotals(requestParams);
            return response.data;
          },
        });
        this.controlSummary = data.summary || null;
        this.controlStatutory = data.statutory || [];
        this.controlOther = data.other_deductions || [];
        this.controlPaymentModes = data.payment_modes || [];
        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      } finally {
        this.controlLoading = false;
      }
    },

    async exportPayrollControlTotals(format = "csv", params = {}) {
      try {
        const svc =
          format === "pdf"
            ? reportsService.exportPayrollControlTotalsPDF
            : reportsService.exportPayrollControlTotalsCSV;

        const res = await svc(params);
        const mime =
          format === "pdf" ? "application/pdf" : "text/csv;charset=utf-8";

        const blob = new Blob([res.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        const period = params.period || "period";
        const ext = format === "pdf" ? "pdf" : "csv";

        a.href = url;
        a.download = `payroll-control-totals-${period}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },

    async fetchLoansSavingsReport(params = {}) {
      if (this.loansSavingsLoading) return;
      this.loansSavingsLoading = true;
      this.error = null;
      try {
        const { force, requestParams } = splitCacheParams(params);
        const data = await loansSavingsReportCache.get(requestParams, {
          ttlMs: REPORT_TTL,
          force,
          loader: async () => {
            const response = await reportsService.loansSavings(requestParams);
            return response.data;
          },
        });

        this.loansSavingsSummary = data.summary || null;

        const rows = data.rows || {};
        this.loansSavingsRows =
          rows.data || rows.data === undefined ? rows.data || [] : data.rows;
        this.loansSavingsTotal = rows.total || 0;
        this.loansSavingsPageSize = rows.per_page || requestParams.per_page || 25;
        this.loansSavingsCurrent = rows.current_page || requestParams.page || 1;

        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      } finally {
        this.loansSavingsLoading = false;
      }
    },

    async exportLoansSavingsReport(format = "csv", params = {}) {
      try {
        const svc =
          format === "pdf"
            ? reportsService.exportLoansSavingsPDF
            : reportsService.exportLoansSavingsCSV;

        const res = await svc(params);
        const mime =
          format === "pdf" ? "application/pdf" : "text/csv;charset=utf-8";

        const blob = new Blob([res.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        const from = params.from || "start";
        const to = params.to || "end";
        const ext = format === "pdf" ? "pdf" : "csv";

        a.href = url;
        a.download = `loans-savings-report-${from}_to_${to}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },

    async fetchMyPayslips(params = {}) {
      if (this.myPayslipLoading) return;
      this.myPayslipLoading = true;
      this.error = null;

      try {
        const { force, requestParams } = splitCacheParams(params);
        const data = await myPayslipsCache.get(requestParams, {
          ttlMs: REPORT_TTL,
          force,
          loader: async () => {
            const response = await reportsService.myPayslips(requestParams);
            return response.data;
          },
        });

        this.myPayslipSummary = data?.summary || null;
        this.myPayslipSeries = Array.isArray(data?.series) ? data.series : [];

        const rows = data?.rows || {};
        this.myPayslipRows = rows.data || [];
        this.myPayslipTotal = rows.total || 0;
        this.myPayslipPageSize = rows.per_page || requestParams.per_page || 25;
        this.myPayslipCurrent = rows.current_page || requestParams.page || 1;

        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      } finally {
        this.myPayslipLoading = false;
      }
    },

    async downloadMyPayslip(payslipId, meta = {}) {
      try {
        const res = await payrollService.downloadMySlip(payslipId);

        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;

        const period = meta?.period || meta?.period_label || "payslip";
        a.download = `Payslip_${period}.pdf`;

        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },

    async fetchMyP9Years() {
      if (this.myP9Loading) return;
      this.myP9Loading = true;

      try {
        const data = await myP9YearsCache.get(
          {},
          {
            ttlMs: 5 * 60 * 1000,
            loader: async () => {
              const response = await reportsService.myP9Years();
              return response.data;
            },
          },
        );
        this.myP9Years = Array.isArray(data?.years) ? data.years : [];
        this.myP9DefaultYear = data?.default_year || new Date().getFullYear();
        return data;
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      } finally {
        this.myP9Loading = false;
      }
    },

    async downloadMyP9(year) {
      if (this.myP9Downloading) return;
      this.myP9Downloading = true;

      try {
        const res = await reportsService.myP9Download(year);
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `P9_${year}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      } finally {
        this.myP9Downloading = false;
      }
    },

    // =========================
    // Member Savings Report
    // =========================
    async fetchMemberSavingsReport(params = {}) {
      if (this.memberSavingsLoading) return;
      this.memberSavingsLoading = true;
      this.error = null;
      try {
        const { force, requestParams } = splitCacheParams(params);
        const data = await memberSavingsReportCache.get(requestParams, {
          ttlMs: REPORT_TTL,
          force,
          loader: async () => {
            const response = await reportsService.memberSavings(requestParams);
            return response.data;
          },
        });
        this.memberSavingsSummary = data.summary || null;

        const rows = data.rows || {};
        this.memberSavingsRows = rows.data || [];
        this.memberSavingsTotal = rows.total || 0;
        this.memberSavingsPageSize = rows.per_page || requestParams.per_page || 25;
        this.memberSavingsCurrent = rows.current_page || requestParams.page || 1;

        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      } finally {
        this.memberSavingsLoading = false;
      }
    },

    async exportMemberSavingsReport(format = "csv", params = {}) {
      try {
        const svc =
          format === "pdf"
            ? reportsService.exportMemberSavingsPDF
            : reportsService.exportMemberSavingsCSV;

        const res = await svc(params);
        const mime =
          format === "pdf" ? "application/pdf" : "text/csv;charset=utf-8";

        const blob = new Blob([res.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        const from = params.from || "start";
        const to = params.to || "end";
        const ext = format === "pdf" ? "pdf" : "csv";

        a.href = url;
        a.download = `member-savings-${from}_to_${to}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },

    // =========================
    // Loan Disbursements Report
    // =========================
    async fetchLoanDisbursementsReport(params = {}) {
      if (this.loanDisbursementsLoading) return;
      this.loanDisbursementsLoading = true;
      this.error = null;
      try {
        const { force, requestParams } = splitCacheParams(params);
        const data = await loanDisbursementsReportCache.get(requestParams, {
          ttlMs: REPORT_TTL,
          force,
          loader: async () => {
            const response =
              await reportsService.loanDisbursements(requestParams);
            return response.data;
          },
        });
        this.loanDisbursementsSummary = data.summary || null;

        const rows = data.rows || {};
        this.loanDisbursementsRows = rows.data || [];
        this.loanDisbursementsTotal = rows.total || 0;
        this.loanDisbursementsPageSize =
          rows.per_page || requestParams.per_page || 25;
        this.loanDisbursementsCurrent =
          rows.current_page || requestParams.page || 1;

        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      } finally {
        this.loanDisbursementsLoading = false;
      }
    },

    async exportLoanDisbursementsReport(format = "csv", params = {}) {
      try {
        const svc =
          format === "pdf"
            ? reportsService.exportLoanDisbursementsPDF
            : reportsService.exportLoanDisbursementsCSV;

        const res = await svc(params);
        const mime =
          format === "pdf" ? "application/pdf" : "text/csv;charset=utf-8";

        const blob = new Blob([res.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        const from = params.from || "start";
        const to = params.to || "end";
        const ext = format === "pdf" ? "pdf" : "csv";

        a.href = url;
        a.download = `loan-disbursements-${from}_to_${to}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },

    // =========================
    // Loan Repayments Report
    // =========================
    async fetchLoanRepaymentsReport(params = {}) {
      if (this.loanRepaymentsLoading) return;
      this.loanRepaymentsLoading = true;
      this.error = null;
      try {
        const { force, requestParams } = splitCacheParams(params);
        const data = await loanRepaymentsReportCache.get(requestParams, {
          ttlMs: REPORT_TTL,
          force,
          loader: async () => {
            const response = await reportsService.loanRepayments(requestParams);
            return response.data;
          },
        });
        this.loanRepaymentsSummary = data.summary || null;

        const rows = data.rows || {};
        this.loanRepaymentsRows = rows.data || [];
        this.loanRepaymentsTotal = rows.total || 0;
        this.loanRepaymentsPageSize = rows.per_page || requestParams.per_page || 25;
        this.loanRepaymentsCurrent = rows.current_page || requestParams.page || 1;

        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      } finally {
        this.loanRepaymentsLoading = false;
      }
    },

    async exportLoanRepaymentsReport(format = "csv", params = {}) {
      try {
        const svc =
          format === "pdf"
            ? reportsService.exportLoanRepaymentsPDF
            : reportsService.exportLoanRepaymentsCSV;

        const res = await svc(params);
        const mime =
          format === "pdf" ? "application/pdf" : "text/csv;charset=utf-8";

        const blob = new Blob([res.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        const from = params.from || "start";
        const to = params.to || "end";
        const ext = format === "pdf" ? "pdf" : "csv";

        a.href = url;
        a.download = `loan-repayments-${from}_to_${to}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },

    // =========================
    // Monthly Collections Report
    // =========================
    async fetchMonthlyCollectionsReport(params = {}) {
      if (this.monthlyCollectionsLoading) return;
      this.monthlyCollectionsLoading = true;
      this.error = null;
      try {
        const { force, requestParams } = splitCacheParams(params);
        const data = await monthlyCollectionsReportCache.get(requestParams, {
          ttlMs: REPORT_TTL,
          force,
          loader: async () => {
            const response =
              await reportsService.monthlyCollections(requestParams);
            return response.data;
          },
        });

        this.monthlyCollectionsSummary = data.summary || null;
        this.monthlyCollectionsRows = Array.isArray(data.rows) ? data.rows : [];

        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      } finally {
        this.monthlyCollectionsLoading = false;
      }
    },

    async exportMonthlyCollectionsReport(format = "csv", params = {}) {
      try {
        const svc =
          format === "pdf"
            ? reportsService.exportMonthlyCollectionsPDF
            : reportsService.exportMonthlyCollectionsCSV;

        const res = await svc(params);
        const mime =
          format === "pdf" ? "application/pdf" : "text/csv;charset=utf-8";

        const blob = new Blob([res.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        const from = params.from || "start";
        const to = params.to || "end";
        const ext = format === "pdf" ? "pdf" : "csv";

        a.href = url;
        a.download = `monthly-collections-${from}_to_${to}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },

    // =========================
    // Interest Earned Report
    // =========================
    async fetchInterestEarnedReport(params = {}) {
      if (this.interestEarnedLoading) return;
      this.interestEarnedLoading = true;
      this.error = null;
      try {
        const { force, requestParams } = splitCacheParams(params);
        const data = await interestEarnedReportCache.get(requestParams, {
          ttlMs: REPORT_TTL,
          force,
          loader: async () => {
            const response = await reportsService.interestEarned(requestParams);
            return response.data;
          },
        });
        this.interestEarnedSummary = data.summary || null;
        this.interestEarnedRows = Array.isArray(data.rows) ? data.rows : [];
        return data;
      } catch (err) {
        this.error = errMsg(err);
        toast(this.error, "error");
        throw err;
      } finally {
        this.interestEarnedLoading = false;
      }
    },

    async exportInterestEarnedReport(format = "csv", params = {}) {
      try {
        const svc =
          format === "pdf"
            ? reportsService.exportInterestEarnedPDF
            : reportsService.exportInterestEarnedCSV;

        const res = await svc(params);
        const mime =
          format === "pdf" ? "application/pdf" : "text/csv;charset=utf-8";

        const blob = new Blob([res.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        const from = params.from || "start";
        const to = params.to || "end";
        const ext = format === "pdf" ? "pdf" : "csv";

        a.href = url;
        a.download = `interest-earned-${from}_to_${to}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast(errMsg(err), "error");
        throw err;
      }
    },
  },
});
